import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useConfirmation } from '@/contexts/confirmation'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getJobs, deleteJobByID, publicJob, unPublicJob } from '../../services'
import { Job } from '../../types'
import { getJobColumns, getJobActions, getJobFilters } from './job-list-config'

interface JobFilters {
    status: string
    locale: string
}

/**
 * Business logic hook for Job List
 */
export const useJobList = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()

    // URL params & filters
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<JobFilters>({
        defaults: { sort_by: 'job_id', sort_order: 'desc' },
        filterDefaults: {
            status: '',
            locale: '',
        },
    })

    // Form for filter controls
    const { control } = useForm<JobFilters>({
        defaultValues: filters,
    })

    // Fetch jobs data
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.JOBS, queryParams],
        queryFn: () => getJobs(queryParams),
        placeholderData: true,
    })

    // Mutations
    const { mutateAsync: doPublicJob, isPending: inPublicProcessing } =
        useMutationWithHandler(
            { mutationFn: publicJob },
            {
                reactQueryKeys: QUERY_KEYS.JOBS,
                successMessage: t('public-job-success'),
                onSuccess: () => refetch(),
            }
        )

    const { mutateAsync: doUnPublicJob, isPending: inUnPublicProcessing } =
        useMutationWithHandler(
            { mutationFn: unPublicJob },
            {
                reactQueryKeys: QUERY_KEYS.JOBS,
                successMessage: t('unpublic-job-success'),
                onSuccess: () => refetch(),
            }
        )

    const { mutateAsync: doDeleteJob, isPending: inDeleteProcessing } =
        useMutationWithHandler(
            { mutationFn: deleteJobByID },
            {
                reactQueryKeys: QUERY_KEYS.JOBS,
                successMessage: t('delete-success'),
                onSuccess: () => refetch(),
            }
        )

    // Handlers
    const handleEdit = useCallback(
        (row: Job) => navigate(`/jobs/${row?.job_id}`),
        [navigate]
    )

    const handleTogglePublic = useCallback(
        (row: Job) => {
            if (row?.public) {
                doUnPublicJob(row.job_id)
            } else {
                doPublicJob(row.job_id)
            }
        },
        [doPublicJob, doUnPublicJob]
    )

    const handleDelete = useCallback(
        (row: Job) => {
            setConfirmation({
                open: true,
                title: t('job-title-delete'),
                description: `${t('job-description-delete')} "${row?.title}"?`,
                onConfirm: () => doDeleteJob(row?.job_id),
            })
        },
        [t, setConfirmation, doDeleteJob]
    )

    const handleCreateNew = useCallback(() => navigate('/jobs/new'), [navigate])

    const handleManageCategories = useCallback(
        () => navigate('/job-categories'),
        [navigate]
    )

    // Memoized configs
    const columns = useMemo(() => getJobColumns(t), [t])

    const actions = useMemo(
        () =>
            getJobActions({
                t,
                onEdit: handleEdit,
                onTogglePublic: handleTogglePublic,
                onDelete: handleDelete,
                isPublicProcessing:
                    inPublicProcessing ||
                    inUnPublicProcessing ||
                    inDeleteProcessing,
            }),
        [
            t,
            handleEdit,
            handleTogglePublic,
            handleDelete,
            inPublicProcessing,
            inUnPublicProcessing,
            inDeleteProcessing,
        ]
    )

    const filterItems = useMemo(() => getJobFilters(t), [t])

    // Pagination & sorting handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        jobs: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        // Params & filters
        params,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        actions,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        handleCreateNew,
        handleManageCategories,

        // Translation
        t,
    }
}
